import { navigateToApp } from '../../auth/appNavigation';
import { appUrl } from '../../config/appUrl';
import { ChevronLeft, ChevronRight, Coffee, Heart, Navigation } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { LandingPlace } from './types';
import { formatDistance, track } from './types';

// Keep each place's highlight stable while swiping and rerendering this session.
const highlightSeed = Math.floor(Math.random() * 0x40000000);
function reviewHighlight(place: LandingPlace): string {
  const bullets = (place.review_summary?.bullets_th ?? [])
    .filter((line): line is string => typeof line === 'string' && Boolean(line.trim()))
    .map(line => line.trim()).slice(0, 4);
  if (!bullets.length) return '';
  let hash = highlightSeed;
  for (const char of place.place_id) hash = Math.imul(hash, 31) + char.charCodeAt(0) | 0;
  return bullets[(hash >>> 0) % bullets.length];
}

function visibleTopic(label: string) {
  const key = label.toLowerCase().replace(/[^a-z0-9ก-๙]/g, '');
  return !new Set(['s', 'small', 'ssmall', 'm', 'medium', 'mmedium', 'l', 'large', 'llarge', 'iced', 'เย็น', 'เย็นiced', 'icedเย็น', 'เล็ก', 'กลาง', 'ใหญ่', 'sizesmall', 'sizemedium', 'sizelarge', 'cupsizesmall', 'cupsizemedium', 'cupsizelarge']).has(key);
}

const SAVED_KEY = 'beaw.landing.saved-places';

function readSaved(): string[] {
  try { const value = JSON.parse(localStorage.getItem(SAVED_KEY) || '[]'); return Array.isArray(value) ? value : []; } catch { return []; }
}

function CardPhoto({ src }: { src: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return src && !failed ? <img src={src} alt="" decoding="async" referrerPolicy="no-referrer" draggable="false" onError={() => setFailed(true)} /> : <span className="hx-card__fallback"><Coffee /></span>;
}

/** Offset of `index` from `active` on a ring of `count`, in -count/2..count/2. */
function ringOffset(index: number, active: number, count: number) {
  let offset = index - active;
  if (offset > count / 2) offset -= count;
  if (offset < -count / 2) offset += count;
  return offset;
}

// Centre card, then each step outwards: horizontal shift, drop, scale, tilt.
const slots = [
  { x: 0, y: 0, scale: 1, tilt: 0 },
  { x: 345, y: 30, scale: .76, tilt: 2 },
  { x: 640, y: 34, scale: .74, tilt: 1.5 },
  { x: 920, y: 40, scale: .72, tilt: 1 },
];

export function PlaceCarousel({ places, activeIndex, onActiveIndex, cityLabel }: { places: LandingPlace[]; activeIndex: number; onActiveIndex: (index: number) => void; cityLabel: string }) {
  const [saved, setSaved] = useState<string[]>(readSaved);
  const drag = useRef<{ x: number; moved: boolean } | undefined>(undefined);
  // Live finger offset while swiping, so the cards follow the drag.
  const [dragX, setDragX] = useState(0);
  const suppressClickUntil = useRef(0);
  const count = places.length;
  if (!count) return null;
  const go = (step: number) => onActiveIndex((activeIndex + step + count) % count);
  const toggleSaved = (id: string) => {
    const next = saved.includes(id) ? saved.filter(item => item !== id) : [...saved, id];
    setSaved(next);
    try { localStorage.setItem(SAVED_KEY, JSON.stringify(next)); } catch { /* Keep this session's selection. */ }
  };
  const open = (place: LandingPlace) => {
    if (Date.now() < suppressClickUntil.current) return;
    track('landing_place_card_click', { place_id: place.place_id, city: cityLabel });
    void navigateToApp(appUrl(`/cafes/${encodeURIComponent(place.place_id)}`));
  };

  return <section className="hx-carousel" aria-roledescription="carousel" aria-label="ร้านใกล้คุณ">
    <div className={`hx-carousel__track ${dragX ? 'is-dragging' : ''}`}
      onPointerDown={event => { if (event.button === 0 && !(event.target as HTMLElement).closest('button')) drag.current = { x: event.clientX, moved: false }; }}
      onPointerMove={event => {
        const start = drag.current;
        if (!start) return;
        const dx = event.clientX - start.x;
        if (!start.moved && Math.abs(dx) > 8) { start.moved = true; event.currentTarget.setPointerCapture(event.pointerId); }
        if (start.moved) setDragX(dx);
      }}
      onPointerUp={event => {
        const start = drag.current; drag.current = undefined;
        setDragX(0);
        if (!start?.moved) return;
        suppressClickUntil.current = Date.now() + 350;
        const dx = event.clientX - start.x;
        if (Math.abs(dx) > Math.min(70, event.currentTarget.clientWidth * .15)) go(dx < 0 ? 1 : -1);
      }}
      onPointerCancel={() => { drag.current = undefined; setDragX(0); }}>
      {places.map((place, index) => {
        const offset = ringOffset(index, activeIndex, count);
        const depth = Math.min(Math.abs(offset), slots.length - 1);
        const slot = slots[depth];
        const side = Math.sign(offset);
        const style = {
          transform: `translateX(calc(-50% + ${side * slot.x} * var(--hx-unit, 1px) + ${dragX}px)) translateY(calc(${slot.y} * var(--hx-unit, 1px))) scale(${slot.scale}) rotate(${side * slot.tilt}deg)`,
          zIndex: 10 - depth,
          opacity: Math.abs(offset) >= slots.length ? 0 : 1,
          pointerEvents: Math.abs(offset) >= slots.length ? 'none' : undefined,
        } as CSSProperties;
        const active = offset === 0;
        const tags = (place.tags ?? []).filter(visibleTopic).slice(0, 4);
        return <article key={place.place_id} className={`hx-card ${active ? 'is-active' : ''}`} style={style} aria-hidden={!active} onClick={() => active ? open(place) : onActiveIndex(index)}>
          <div className="hx-card__photo">
            <CardPhoto src={place.image_url} />
            {place.distance_m !== null && <span className="hx-card__distance"><Navigation />{formatDistance(place.distance_m)}</span>}
            <button type="button" className="hx-card__save" aria-label={saved.includes(place.place_id) ? 'เลิกบันทึกร้าน' : 'บันทึกร้าน'} aria-pressed={saved.includes(place.place_id)} tabIndex={active ? 0 : -1} onClick={event => { event.stopPropagation(); toggleSaved(place.place_id); }}><Heart fill={saved.includes(place.place_id) ? 'currentColor' : 'none'} /></button>
          </div>
          <div className="hx-card__body">
            <div className="hx-card__title"><span className="hx-card__icon"><Coffee /></span><span><b>{place.name}</b><small>{[`${place.review_count.toLocaleString('th-TH')} รีวิว`, reviewHighlight(place)].filter(Boolean).join(' · ')}</small></span></div>
            {Boolean(tags.length) && <div className="hx-card__tags">{tags.map(tag => <span key={tag}>{tag}</span>)}</div>}
          </div>
        </article>;
      })}
    </div>
    {count > 1 && <>
      <button type="button" className="hx-carousel__arrow hx-carousel__arrow--prev" aria-label="ร้านก่อนหน้า" onClick={() => go(-1)}><ChevronLeft /></button>
      <button type="button" className="hx-carousel__arrow hx-carousel__arrow--next" aria-label="ร้านถัดไป" onClick={() => go(1)}><ChevronRight /></button>
      <div className="hx-carousel__dots" role="tablist" aria-label="เลือกร้าน">{places.map((place, index) => <button type="button" role="tab" key={place.place_id} aria-selected={index === activeIndex} aria-label={place.name} className={index === activeIndex ? 'is-active' : ''} onClick={() => onActiveIndex(index)} />)}</div>
    </>}
  </section>;
}
